import React, { useState, useRef, useEffect } from "react";
import { bindActionCreators } from "redux";
import { connect } from "react-redux";
import moment from "moment";
import { useIntl, FormattedMessage, FormattedDate, defineMessages } from "react-intl";
import { Typography } from "@material-ui/core";
import { createTheme, ThemeProvider } from "@material-ui/core/styles";
import { DateRangePicker, DateRangeDelimiter } from "@material-ui/pickers";

import { setDateDomain } from "../../redux/domain/domainActions";
import SmallDropdownIcon from "../common/icons/arrows/smallDropdownIcon";
import ClickAwayListener from "@material-ui/core/ClickAwayListener";
import "./dateDomain.css";

const messages = defineMessages({
    cancel: {
        id: "select-date-domain.cancel-caption",
        defaultMessage: "Cancel",
        description: "Cancel selected date range",
    },
    accept: {
        id: "select-date-domain.accept-caption",
        defaultMessage: "Apply",
        description: "Apply selected date range",
    },
});

const theme = createTheme({
    overrides: {
        MuiTypography: {
            root: {
                color: "var(--blue-bell)",
                fontFamily: "Arial",
                fontSize: "14px",
                fontStyle: "normal",
                fontWeight: "400",
                lineHeight: "14px",
                letterSpacing: "0px",
                display: "inline",
            },
        },
        MuiPickersDay: {
            root: {
                "&$selected": {
                    backgroundColor: "var(--blue-medium)",
                },
            },
            today: {
                fontWeight: "700",
                color: "var(--blue-status)",
                border: "none !important",
            },
            dayOutsideMonth: {
                backgroundColor: "var(--grey-background) !important",
                borderRadius: "unset",
            },
        },
        MuiPickersDateRangePickerInput: {
            root: {
                display: "table-cell",
                paddingLeft: "9px",
            },
        },
        MuiPickersDateRangeDay: {
            rangeIntervalDayHighlight: {
                backgroundColor: "var(--blue-bell)",
            },
            day: {},
        },
        MuiDialogActions: {
            root: {
                display: "unset",
            },
        },
    },
});

export const DateDomain = ({ dateRange, setDateDomain }) => {
    const [value, setValue] = useState([dateRange.start, dateRange.end]);
    const [applyButton, setApplyButton] = useState(null);
    const [cancelButton, setCancelButton] = useState(null);
    const [open, setOpen] = useState(false);
    const intl = useIntl();

    // update the date value state when the current dateRange property changes
    useEffect(() => {
        setValue([dateRange.start, dateRange.end]);
    }, [dateRange]);

    // keep an instance of the apply button in
    // state so we can apply the correct value when updating
    // the date domain
    useEffect(() => {
        if (applyButton !== null) {
            applyButton.onclick = () => {
                updateDateDomain();
            };

            applyButton.innerText = intl.formatMessage(messages.accept);
        }

        if (cancelButton !== null) {
            cancelButton.innerText = intl.formatMessage(messages.cancel);
        }
    });

    // use a reference to the popper to add our
    // cancel and apply buttons to the date range picker
    const popperRef = useRef(null);
    const setPopperRef = (ref) => {
        popperRef.current = ref;

        if (popperRef.current !== null) {
            const addButtons = (calContainer) => {
                const selectedButtonDiv = calContainer.querySelector(".bell-daterange-btns");

                if (selectedButtonDiv) {
                    return;
                }
                const buttonDiv = document.createElement("div");
                buttonDiv.className = "bell-daterange-btns";

                const applyButton = document.createElement("button");
                const cancelButton = document.createElement("button");

                applyButton.className = "bell-btn primary";
                cancelButton.className = "bell-btn secondary";

                // keep track of buttons so we can
                // change the language
                setApplyButton(applyButton);
                setCancelButton(cancelButton);

                cancelButton.onclick = () => setOpen(false);

                buttonDiv.appendChild(cancelButton);
                buttonDiv.appendChild(applyButton);
                calContainer.appendChild(buttonDiv);
            };

            const getCalendarContainer = () => {
                const calContainer = popperRef.current.popper.querySelector("div > div > div > div > div");

                if (calContainer) {
                    addButtons(calContainer);
                } else {
                    setTimeout(getCalendarContainer, 100);
                }
            };

            getCalendarContainer();
        }
    };

    // update the domain state and close the date range picker
    const updateDateDomain = () => {
        setDateDomain(...value);
        setOpen(false);
    };

    const dateFormat = "MMM DD, yyyy";
    const start = moment(dateRange.start).format(dateFormat);
    const end = dateRange.end ? moment(dateRange.end).format(dateFormat) : null;

    return (
        <ThemeProvider theme={theme}>
            <ClickAwayListener onClickAway={() => setOpen(false)}>
                <div>
                    <DateRangePicker
                        id="date-domain-picker"
                        calendars={1}
                        value={value}
                        open={open}
                        inputFormat={"MMM dd, yyyy"}
                        onClose={() => setOpen(false)}
                        onChange={(newValue) => setValue(newValue)}
                        onAccept={() => updateDateDomain()}
                        cancelText={intl.formatMessage(messages.cancel)}
                        okText={intl.formatMessage(messages.accept)}
                        showToolbar={false}
                        disableCloseOnSelect={true}
                        showDaysOutsideCurrentMonth={true}
                        allowSameDateSelection={true}
                        PopperProps={{
                            id: "date-domain-picker",
                            popperRef: setPopperRef,
                        }}
                        mask=""
                        renderInput={(startProps, endProps) => (
                            <div onClick={() => setOpen(true)}>
                                <Typography display="inline">
                                    <FormattedMessage
                                        id="select-date-domain.title"
                                        defaultMessage="Select date range: "
                                        description="Select date range caption"
                                    />
                                </Typography>
                                <Typography display="inline">
                                    {/* <FormattedDate
                                        value={dateRange.start}
                                        year="numeric"
                                        month="long"
                                        day="2-digit"
                                    /> */}
                                    {moment(dateRange.start)?.format("MMM DD, YYYY")}
                                </Typography>
                                {end && start !== end && (
                                    <>
                                        <DateRangeDelimiter>-</DateRangeDelimiter>
                                        <Typography display="inline">
                                            {/* <FormattedDate
                                                value={dateRange.end}
                                                year="numeric"
                                                month="long"
                                                day="2-digit"
                                            /> */}
                                            {moment(dateRange.end)?.format("MMM DD, YYYY")}
                                        </Typography>
                                    </>
                                )}
                                <SmallDropdownIcon />
                            </div>
                        )}
                    />
                </div>
            </ClickAwayListener>
        </ThemeProvider>
    );
};

const mapStateToProps = (state) => ({
    dateRange: state.domain.dateRange,
});

const mapDispatchToProps = (dispatch) =>
    bindActionCreators(
        {
            setDateDomain,
        },
        dispatch,
    );

export default connect(mapStateToProps, mapDispatchToProps)(DateDomain);
